import { Typography } from '../Typography'
import { Icons } from '..'
import { VariantProps, tv } from 'tailwind-variants'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'

const selectVariants = tv({
  slots: {
    trigger:
      'w-full text-gray-300 outline-none bg-gray-800 rounded-lg px-2 h-10',
    triggerText: 'flex w-full items-center justify-between text-gray-400',
    content:
      'z-10 max-h-60 custom-scrollbar rounded-md bg-gray-850 w-full absolute left-0 right-0 py-2 shadow-md shadow-gray-800 origin-top transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 overflow-hidden',
    optionItem: 'outline-none hover:bg-gray-700 w-full text-left px-4 py-2',
  },
  variants: {
    isSelected: {
      true: {
        triggerText: 'text-white/50',
        optionItem: 'bg-gray-700/80',
      },
    },
    isDisabled: {
      true: {
        triggerText: 'opacity-50',
      },
    },
    isInvalid: {
      true: {
        trigger: 'border-danger-500 !border shadow-danger-500 shadow-sm',
      },
    },
  },
})

type TOptionItem = {
  title: string
  value: string
}

type TSelectProps = {
  options: TOptionItem[]
  placeholder: string
  value?: TOptionItem | string
  onChange(value: TOptionItem): void
  message?: string
} & VariantProps<typeof selectVariants>

export function Select({
  options,
  onChange,
  value,
  placeholder,
  isDisabled,
  message,
  isInvalid,
}: TSelectProps) {
  const { trigger, triggerText, content, optionItem } = selectVariants({
    isSelected: !!value,
    isDisabled,
    isInvalid,
  })

  return (
    <div className="relative w-full">
      <Menu>
        <MenuButton
          disabled={isDisabled}
          data-testid="select_trigger"
          className={trigger()}>
          <Typography className={triggerText()}>
            {value && typeof value === 'object' ? value.title : placeholder}
            <Icons.ArrowDownSelect />
          </Typography>
        </MenuButton>
        <MenuItems transition className={content()}>
          {options.map((item) => (
            <MenuItem key={item.value}>
              <button
                type="button"
                className={optionItem({
                  isSelected:
                    value && typeof value === 'object'
                      ? value?.value === item.value
                      : false,
                })}
                onClick={() => onChange(item)}>
                <Typography type="bodyTwo" className="text-white/70">
                  {item.title}
                </Typography>
              </button>
            </MenuItem>
          ))}
        </MenuItems>
      </Menu>
      {message && (
        <Typography type="caption" className="text-danger-500">
          {message}
        </Typography>
      )}
    </div>
  )
}
